import React from 'react';
import { CustomModal, useCustomToast } from '../../../../components';
import { Item, TransfersRequest } from '../../types/transfers-response';
import { useAtom } from 'jotai';
import { useCreateTransfer } from '../../../../hooks';
import { useGoodStates, useSupervisors, useGetAddittions, useAssets } from '../../../../share';
import { authAtom } from '../../../../state';
import { UploadedFile } from '../../../../utils';
import TransferCreateView from '../Views/TransfersCreateView';
import useTransferRequestFormik from '../../../../utils/validators/transfer-request/transfer-request-validator';
import ModalSaveChanges from '../../../../components/ModalSaveChanges/ModalSaveChanges';

interface TransfersCreateProps {
  isOpen: boolean;
  OnClose: () => void;
}

const TransfersCreateContainer: React.FC<TransfersCreateProps> = ({
  isOpen,
  OnClose,
}) => {
  const [files, setFiles] = React.useState<UploadedFile[]>();
  const [modalSignConfirmOpen, setModalSignConfirmOpen] = React.useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const { showToast } = useCustomToast();

  const [authData] = useAtom(authAtom);
  const { mutateAsync, isPending: loadCreate } = useCreateTransfer();
  const { data: goodStates } = useGoodStates();
  const { data: supervisors } = useSupervisors({ branch_id: authData?.user.branch_id });
  const { data: assetList } = useAssets({ responsible_id: authData?.user.id, role_service: '1', asset_status_id: '1' });
  const { mutateAsync: getAddittions } = useGetAddittions();

  const onSave = async (values: Partial<TransfersRequest>) => {
    const payload: Partial<TransfersRequest> = {
      dependency_id: values.dependency_id,
      new_dependency_id: values.new_dependency_id,
      new_responsible_id: values.new_responsible_id,
      observations: values.observations,
      documents: files,
      items: selectedRowKeys?.map((value) => ({ asset_id: value } as Item)),
      query_date: values.query_date,
      signature_url: '' ,
    };

    await mutateAsync({
      ...payload as TransfersRequest
    });
    onReset();
  };

  const formik = useTransferRequestFormik({ onSubmit: onSave });

  const handleSubmit = () => {
    if (!selectedRowKeys.length) {
      showToast('error', 'Debes seleccionar items');
      return;
    }

    if (!formik.isValid) {
      formik.handleSubmit();
      return;
    }
    
    if (formik.isValid && modalSignConfirmOpen) {
      formik.handleSubmit();
    }
 
    setModalSignConfirmOpen(!modalSignConfirmOpen);
  };

  const onReset = () => {
    formik.resetForm();
    OnClose();
    setFiles([]);
    setSelectedRowKeys([]);
  };

  const onSelectNewResponsable = (user: string) => {
    formik.setFieldValue('new_responsible_id', user)
  };

  const getAddittionsFromAsset = (assetId: string) => getAddittions({assetId});

  return (
    <CustomModal
      title="Nueva solicitud de traslado de funcionarios"
      labelSubmit="Guardar solicitud"
      open={isOpen}
      onCancel={() => {
        onReset();
      }}
      width="95vw"
      isLoadingSave={loadCreate}
      onSave={handleSubmit}
    >
      <TransferCreateView
        isOpen={isOpen}
        goodStates={goodStates}
        authData={authData}
        supervisors={supervisors}
        formik={formik}
        onFiles={setFiles}
        files={files}
        assets={assetList}
        onSelectNewResponsable={onSelectNewResponsable}
        getAddittions={getAddittionsFromAsset}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      
      <ModalSaveChanges
        title="Guardar cambios"
        textOne=" ¿Está seguro de crear esta solicitud de traslado?"
        textTwo="Una vez creado no podrá realizar cambios."
        open={modalSignConfirmOpen}
        width={900}
        isLoadingSave={false}
        onClose={() => setModalSignConfirmOpen(false)}
        labelSubmit="Aceptar"
        onSave={handleSubmit} />
    </CustomModal>
  );
};

export default TransfersCreateContainer;
